import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ListBox, Select } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { LedgerHandoffNotice } from '@/pages/accounting/components/ledgerHandoffNotice';
import {
    ClosePeriodDialog,
    EntryPreviewDialog,
    PeriodTable,
    ReopenYearDialog,
    YearEndPanel,
} from './components';
import { getErrorReasonKey } from './periodsMeta';
import type { PeriodsPageModel } from './usePeriodsPage';

type PeriodsPageViewProps = {
    model: PeriodsPageModel;
};

/**
 * Periods and year-end close. Render-only — every derivation lives in
 * `usePeriodsPage` / `periodsMeta`.
 *
 * No page title and no page-root padding: the shell supplies both.
 */
export const PeriodsPageView: React.FC<PeriodsPageViewProps> = ({ model }) => {
    const { t } = useTranslation('pages');

    const actionErrorKey = getErrorReasonKey(model.actionError);
    const actionErrorText = model.actionError
        ? actionErrorKey
            ? t(actionErrorKey)
            : (model.actionError.message ?? t('periods.errors.generic'))
        : null;

    const closePreviewErrorKey = getErrorReasonKey(model.closePreviewError);

    // A load error is thrown to the page's ErrorBoundary, so it is never
    // rendered here.

    return (
        <div
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('periods-section')}
        >
            {/* Fiscal-year selector */}
            <div className="flex flex-wrap items-center gap-3">
                <Select
                    aria-label={t('periods.fiscalYearAria')}
                    className="w-48"
                    isDisabled={model.isLoading}
                    onChange={(value) => model.onSelectYear(String(value))}
                    value={model.selectedYearId}
                >
                    <Select.Trigger
                        {...getNodeTestIdProp('periods-fiscal-year')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {model.fiscalYears.map((year) => (
                                <ListBox.Item
                                    id={year.id}
                                    key={year.id}
                                    textValue={year.label}
                                    {...getNodeTestIdProp(
                                        `periods-fiscal-year-${year.id}`,
                                    )}
                                >
                                    {year.label}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>
            </div>

            <YearEndPanel
                fiscalYear={model.selectedYear}
                onCloseYear={() => model.onOpenDialog({ kind: 'CLOSE_YEAR' })}
                onDismissResult={model.onDismissResult}
                onReopenYear={() => model.onOpenDialog({ kind: 'REOPEN_YEAR' })}
                onRollover={() => model.onOpenDialog({ kind: 'ROLLOVER' })}
                result={model.result}
                rollover={model.rollover}
                yearClose={model.yearClose}
            />

            {!model.isLoading && model.periods.length === 0 ? (
                <div
                    className="flex flex-col items-center gap-3 rounded-xl border border-separator py-12 text-muted"
                    {...getNodeTestIdProp('periods-empty')}
                >
                    <Icon
                        aria-hidden
                        className="size-8 text-default-500"
                        icon="gravity-ui:calendar"
                    />
                    <span className="text-sm">{t('periods.empty')}</span>
                </div>
            ) : (
                <>
                    {/* Stated once (BOS-170). It used to be repeated on every
                        blocked row — eleven copies of one rule on a year with
                        eleven open months, which the reader learns to scroll
                        past, including the row whose reason is different. */}
                    {model.showsClosesInOrderNotice ? (
                        <p
                            className="text-sm text-muted"
                            {...getNodeTestIdProp('periods-order-notice')}
                        >
                            {t('periods.reasons.closesInOrderNotice')}
                        </p>
                    ) : null}
                    <PeriodTable
                        earlierOpenCounts={model.earlierOpenCounts}
                        isLoading={model.isLoading}
                        isYearSettled={model.isYearSettled}
                        laterClosedCounts={model.laterClosedCounts}
                        onClose={(period) =>
                            model.onOpenDialog({ kind: 'CLOSE_PERIOD', period })
                        }
                        onReopen={(period) =>
                            model.onOpenDialog({
                                kind: 'REOPEN_PERIOD',
                                period,
                            })
                        }
                        periods={model.periods}
                    />
                </>
            )}

            {/* Close / reopen one month */}
            <ClosePeriodDialog
                errorMessage={actionErrorText}
                isPending={model.isClosingPeriod || model.isReopeningPeriod}
                isReopen={model.dialog?.kind === 'REOPEN_PERIOD'}
                onCancel={model.onCloseDialog}
                onConfirm={
                    model.dialog?.kind === 'REOPEN_PERIOD'
                        ? model.onConfirmReopenPeriod
                        : model.onConfirmClosePeriod
                }
                period={
                    model.dialog?.kind === 'CLOSE_PERIOD' ||
                    model.dialog?.kind === 'REOPEN_PERIOD'
                        ? model.dialog.period
                        : null
                }
            />

            {/* Year-end close — preview, then commit */}
            <EntryPreviewDialog
                commitError={actionErrorText}
                isLoading={model.isClosePreviewLoading}
                isOpen={model.dialog?.kind === 'CLOSE_YEAR'}
                isPending={model.isClosingYear}
                kind="CLOSE_YEAR"
                locale={model.locale}
                onCancel={model.onCloseDialog}
                onConfirm={model.onConfirmCloseYear}
                preview={model.closePreview}
                previewError={
                    model.closePreviewError
                        ? closePreviewErrorKey
                            ? t(closePreviewErrorKey)
                            : (model.closePreviewError.message ??
                              t('periods.errors.generic'))
                        : null
                }
            />

            {/* Retained-earnings rollover — the one action on this screen that
                stays closed. Its commit endpoint is real, but the API has no
                `/rollover/preview`, so there is nothing to approve before
                posting a real ROLLOVER entry. Both sides must be real. */}
            <LedgerHandoffNotice
                isOpen={model.dialog?.kind === 'ROLLOVER'}
                onClose={model.onCloseDialog}
            />

            {/* Reopen the year by reversing its closing entry */}
            <ReopenYearDialog
                errorMessage={actionErrorText}
                fiscalYear={model.selectedYear}
                isOpen={model.dialog?.kind === 'REOPEN_YEAR'}
                isPending={model.isReopeningYear}
                onCancel={model.onCloseDialog}
                onConfirm={model.onConfirmReopenYear}
            />
        </div>
    );
};
